import { PassengerType } from "@b2bportal/core-types";
import { getPassengerType } from "@b2bportal/core-utilities";
import type { Travelers } from "@b2bportal/packages-api";
import { getPackageFlightPassengerCounts } from "@/store/packages/thunks/flightPassengerUtils";

/**
 * Pure logic for package checkout traveler validation.
 *
 * Kept separate from usePackageTravelerCountValidation so it can be unit-tested
 * without importing @checkout/index (which triggers the known RBC module
 * init-order issue).
 */
export interface PackageTravelerComposition {
  adults: number;
  children: number;
  infantsOnLap: number;
  infantsInSeat: number;
}

/**
 * Expected flight passenger breakdown for the package search, using the same
 * age bands as the flight shop request so validation matches what was priced.
 */
export const getExpectedTravelerComposition = (
  guests: Travelers
): PackageTravelerComposition => {
  const { ADT, CNN, INF, INS } = getPackageFlightPassengerCounts(guests);
  return { adults: ADT, children: CNN, infantsOnLap: INF, infantsInSeat: INS };
};

/**
 * Classifies selected travelers using flight passenger age bands
 * (adult 12+, child 2-11, infant under 2) on the trip date. Seated travelers
 * under 2 are infants in seat; lap infants are tracked separately by the
 * checkout machine.
 */
export const getSelectedTravelerComposition = ({
  seatedTravelers,
  lapInfantCount,
  tripDate,
}: {
  seatedTravelers: ReadonlyArray<{ dateOfBirth: string }>;
  lapInfantCount: number;
  tripDate?: string;
}): PackageTravelerComposition =>
  seatedTravelers.reduce<PackageTravelerComposition>(
    (composition, { dateOfBirth }) => {
      switch (getPassengerType({ dateOfBirth, tripDate })) {
        case PassengerType.Adult:
          return { ...composition, adults: composition.adults + 1 };
        case PassengerType.Child:
          return { ...composition, children: composition.children + 1 };
        default:
          return {
            ...composition,
            infantsInSeat: composition.infantsInSeat + 1,
          };
      }
    },
    { adults: 0, children: 0, infantsOnLap: lapInfantCount, infantsInSeat: 0 }
  );

export type InfantSeatType = "OnLap" | "OwnSeat";

/**
 * Suggests the seat type for the next infant being selected, based on which
 * searched infant slots are still unfilled. Prefers lap when both are open,
 * and returns undefined when the search has no remaining infant slots.
 */
export const getSuggestedInfantSeatType = (
  expected: PackageTravelerComposition,
  selected: PackageTravelerComposition
): InfantSeatType | undefined => {
  const lapRemaining = expected.infantsOnLap - selected.infantsOnLap;
  const seatRemaining = expected.infantsInSeat - selected.infantsInSeat;
  if (lapRemaining > 0) return "OnLap";
  if (seatRemaining > 0) return "OwnSeat";
  return undefined;
};

export const isSameTravelerComposition = (
  a: PackageTravelerComposition,
  b: PackageTravelerComposition
): boolean =>
  a.adults === b.adults &&
  a.children === b.children &&
  a.infantsOnLap === b.infantsOnLap &&
  a.infantsInSeat === b.infantsInSeat;
